import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  allLeads: [],
  filteredLeads: [],
  currentFilters: {},
  searchTerm: '',
  currentLeadIndex: -1,
  loading: false,
  error: null,
};

const leadsSlice = createSlice({
  name: 'leads',
  initialState,
  reducers: {
    setAllLeads: (state, action) => {
      state.allLeads = action.payload;
      // If no filtered leads exist, set filtered leads to all leads
      if (state.filteredLeads.length === 0) {
        state.filteredLeads = action.payload;
      }
    },
    setFilteredLeads: (state, action) => {
      state.filteredLeads = action.payload;
      // Reset current lead index when filtered leads change
      state.currentLeadIndex = -1;
    },
    setCurrentFilters: (state, action) => {
      state.currentFilters = action.payload;
    },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    setCurrentLeadIndex: (state, action) => {
      state.currentLeadIndex = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearFilters: (state) => {
      state.currentFilters = {};
      state.searchTerm = '';
      state.filteredLeads = state.allLeads;
      state.currentLeadIndex = -1;
    },
    updateLead: (state, action) => {
      const updatedLead = action.payload;
      if (!updatedLead || !updatedLead._id) return;
      
      // Helper to compare IDs (handle both string and ObjectId)
      const compareIds = (id1, id2) => {
        const str1 = id1?.toString ? id1.toString() : id1;
        const str2 = id2?.toString ? id2.toString() : id2;
        return str1 === str2;
      };
      
      // Update in all leads
      const allLeadsIndex = state.allLeads.findIndex(lead => compareIds(lead._id, updatedLead._id));
      if (allLeadsIndex !== -1) {
        state.allLeads[allLeadsIndex] = updatedLead;
      }
      // Update in filtered leads
      const filteredLeadsIndex = state.filteredLeads.findIndex(lead => compareIds(lead._id, updatedLead._id));
      if (filteredLeadsIndex !== -1) {
        state.filteredLeads[filteredLeadsIndex] = updatedLead;
      }
    },
    deleteLead: (state, action) => {
      const leadId = action.payload;
      state.allLeads = state.allLeads.filter(lead => lead._id !== leadId);
      state.filteredLeads = state.filteredLeads.filter(lead => lead._id !== leadId);
      // Adjust current lead index if necessary
      if (state.currentLeadIndex >= state.filteredLeads.length) {
        state.currentLeadIndex = state.filteredLeads.length - 1;
      }
    },
    addLead: (state, action) => {
      const newLead = action.payload;
      state.allLeads.unshift(newLead);
      // Add to filtered leads if it matches current filters
      // For simplicity, we'll add it to filtered leads as well
      state.filteredLeads.unshift(newLead);
    },
  },
});

export const {
  setAllLeads,
  setFilteredLeads,
  setCurrentFilters,
  setSearchTerm,
  setCurrentLeadIndex,
  setLoading,
  setError,
  clearFilters,
  updateLead,
  deleteLead,
  addLead,
} = leadsSlice.actions;

// Selectors
export const selectAllLeads = (state) => state.leads.allLeads;
export const selectFilteredLeads = (state) => state.leads.filteredLeads;
export const selectCurrentFilters = (state) => state.leads.currentFilters;
export const selectSearchTerm = (state) => state.leads.searchTerm;
export const selectCurrentLeadIndex = (state) => state.leads.currentLeadIndex;
export const selectCurrentLead = (state) => {
  const { filteredLeads, currentLeadIndex } = state.leads;
  return currentLeadIndex >= 0 && currentLeadIndex < filteredLeads.length 
    ? filteredLeads[currentLeadIndex] 
    : null;
};
export const selectLeadsLoading = (state) => state.leads.loading;
export const selectLeadsError = (state) => state.leads.error;

export default leadsSlice.reducer;